/** TanStack Query: cliente compartilhado e chaves organizadas por recurso. */
import { QueryClient, queryOptions } from "@tanstack/react-query";
import type { RuleKind } from "./api-types";
import { api } from "./api";
import { authClient } from "./auth-client";
import { ApiError } from "./errors";

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        // Não insiste em erros de cliente (4xx); tenta de novo falhas de rede/5xx.
        retry: (count, err) => (err instanceof ApiError && err.status >= 400 && err.status < 500 ? false : count < 2),
      },
      mutations: { retry: false },
    },
  });
}

export const qk = {
  session: ["session"] as const,
  me: ["me"] as const,
  adminOrgs: (q: string) => ["admin", "orgs", q] as const,
  crowdsecOverview: ["admin", "crowdsec", "overview"] as const,
  crowdsecDecisions: (q: string) => ["admin", "crowdsec", "decisions", q] as const,
  crowdsecAlerts: (f: object) => ["admin", "crowdsec", "alerts", f] as const,
  crowdsecAllowlist: ["admin", "crowdsec", "allowlist"] as const,
  plans: ["billing", "plans"] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["projects", id] as const,
  projectRules: (id: string, kind: RuleKind) => ["projects", id, "rules", kind] as const,
  domains: (projectId: string) => ["domains", "list", projectId] as const,
  allDomains: ["domains"] as const,
  domain: (id: string) => ["domains", "detail", id] as const,
  domainEvents: (id: string) => ["domains", "detail", id, "events"] as const,
  domainRules: (id: string, kind: RuleKind, effective: boolean) => ["domains", "detail", id, "rules", kind, effective] as const,
  seo: (id: string) => ["domains", "detail", id, "seo"] as const,
  apiKeys: ["api-keys"] as const,
  tailorkit: (id: string) => ["domains", "detail", id, "tailorkit"] as const,
};

/** Sessão do better-auth (null = deslogado). */
export const sessionQuery = queryOptions({
  queryKey: qk.session,
  queryFn: async () => {
    const res = await authClient.getSession();
    if (res.error) {
      if (res.error.status === 401) return null;
      throw new ApiError(res.error.status ?? 0, "network", res.error.message);
    }
    return res.data ?? null;
  },
  staleTime: 5 * 60_000,
});

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: ({ signal }) => api.me(signal),
  staleTime: 30_000,
});

export const plansQuery = queryOptions({
  queryKey: qk.plans,
  queryFn: () => api.listPlans().then((r) => r.items),
  staleTime: 10 * 60_000,
});

export const projectsQuery = queryOptions({
  queryKey: qk.projects,
  queryFn: () => api.listProjects().then((r) => r.items),
});

export const projectQuery = (id: string) =>
  queryOptions({ queryKey: qk.project(id), queryFn: () => api.getProject(id) });

export const domainQuery = (id: string) => queryOptions({ queryKey: qk.domain(id), queryFn: () => api.getDomain(id) });
